import React, { useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import {
  getIntroNarration,
  getIntroNarrationPlayedKey,
  type IntroNarrationConfig,
} from '../../../config/intro/IntroConfig';

/**
 * IntroNarrationOverlay — narration across the intro-to-Hollow-Coil journey.
 *
 * Consumer of `INTRO_CONFIG.narrationByMap` (the SSOT in IntroConfig). Each
 * map's lines fade in sequence, hold after the last line, then fade out.
 *
 * Design rules enforced here:
 *  - NEVER blocks input — `pointer-events: none`, and the skip listeners are
 *    passive window listeners (any key/click/tap collapses the beat instantly,
 *    the input still reaches the game).
 *  - Fires once per character: the played-flag is a `helix_*` localStorage key,
 *    so the /intro `?reset=1` fresh-character wipe replays it (and a respawn
 *    mid-session does NOT).
 *  - Positions as a cinematic caption in the upper third — below the HUD, out
 *    of the FTUE toast lane at the very top.
 *
 * Trigger: sticky LEVEL_LOADED (replay-on-subscribe, so mounting after the
 * boot emit still fires). Maps without authored narration are ignored.
 */

const FADE_OUT_MS = 600;

const hasPlayed = (mapId: string): boolean => {
  try {
    return typeof localStorage !== 'undefined' &&
      localStorage.getItem(getIntroNarrationPlayedKey(mapId)) === '1';
  } catch { return false; }
};

const markPlayed = (mapId: string): void => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(getIntroNarrationPlayedKey(mapId), '1');
    }
  } catch { /* storage unavailable — plays again next time, harmless */ }
};

export const IntroNarrationOverlay: React.FC = () => {
  const [visibleLines, setVisibleLines] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'playing' | 'fading'>('idle');
  const [narration, setNarration] = useState<IntroNarrationConfig | null>(null);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const phaseRef = useRef(phase);
  const activeMapRef = useRef<string | null>(null);
  phaseRef.current = phase;

  useEffect(() => {
    const clearTimers = () => {
      for (const t of timers.current) clearTimeout(t);
      timers.current = [];
    };

    const dismiss = () => {
      if (phaseRef.current !== 'playing') return;
      // Mark played on DISMISS (skip or natural end), not on start — a scene
      // restart mid-narration remounts this overlay, and marking early would
      // make the lines vanish half-told. An unfinished narration replays once
      // on the remount instead.
      if (activeMapRef.current) markPlayed(activeMapRef.current);
      clearTimers();
      setPhase('fading');
      timers.current.push(setTimeout(() => setPhase('idle'), FADE_OUT_MS));
    };

    const start = (mapId: string, cfg: IntroNarrationConfig) => {
      if (hasPlayed(mapId)) return;
      clearTimers();
      activeMapRef.current = mapId;
      setNarration(cfg);
      setPhase('playing');
      setVisibleLines(1);
      for (let i = 1; i < cfg.lines.length; i++) {
        timers.current.push(setTimeout(() => setVisibleLines(i + 1), i * cfg.lineDelayMs));
      }
      // Auto-dismiss after the full reveal + hold.
      timers.current.push(setTimeout(dismiss, cfg.lines.length * cfg.lineDelayMs + cfg.holdMs));
    };

    // Sticky replay-on-subscribe — fires even if the map loaded before mount.
    const unsubLevel = EventBus.getInstance().on(EVENTS.LEVEL_LOADED, (data: { id: string }) => {
      const cfg = getIntroNarration(data?.id);
      if (cfg?.enabled) start(data.id, cfg);
    });
    const unsubGamepad = EventBus.getInstance().on(EVENTS.GAMEPAD_ACTIVITY, dismiss);

    // Skip: any key or click collapses the beat instantly. Passive window
    // listeners — the input is NOT consumed, play continues uninterrupted.
    const onSkip = () => dismiss();
    window.addEventListener('keydown', onSkip, { passive: true });
    window.addEventListener('pointerdown', onSkip, { passive: true });

    return () => {
      unsubLevel();
      unsubGamepad();
      clearTimers();
      window.removeEventListener('keydown', onSkip);
      window.removeEventListener('pointerdown', onSkip);
    };
  }, []);

  if (phase === 'idle') return null;

  const lines = narration?.lines ?? [];

  return (
    <div
      className="intro-narration-overlay"
      role="status"
      aria-live="polite"
      aria-atomic={false}
      aria-relevant="additions text"
      style={{
        position: 'fixed',
        top: '22%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(680px, 86vw)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '14px',
        zIndex: 950, // below the FTUE toast (1000); above the game canvas
        pointerEvents: 'none', // narration NEVER blocks input
        opacity: phase === 'fading' ? 0 : 1,
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
      }}
    >
      {lines.slice(0, visibleLines).map((line, i) => (
        <div
          key={i}
          className="intro-narration-line"
          style={{
            color: 'rgba(245, 240, 228, 0.96)',
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: i === lines.length - 1 ? '1.15rem' : '1.25rem',
            fontStyle: 'italic',
            textAlign: 'center',
            lineHeight: 1.5,
            textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 0 18px rgba(0,0,0,0.7)',
            animation: 'introLineFadeIn 1.2s ease-out both',
          }}
        >
          {line}
        </div>
      ))}
      <style>{`
        @keyframes introLineFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .intro-narration-overlay {
            transition: none !important;
          }
          .intro-narration-line {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};
